// src/pages/AuthScreen.jsx — Login · Register · Forgot Password · Reset Password · Email OTP
import { useState, useEffect, useCallback } from 'react';
import { auth, authExtra } from '../services/api';
import CountryCodePicker, { COUNTRIES } from '../components/CountryCodePicker';
import Logo from '../components/Logo';
import { G, GD, GL } from '../constants';
import { FEATURES } from '../config/features';

const TABS = ['login', 'register'];
const RESEND_COOLDOWN = 60; // seconds

export default function AuthScreen({ onLogin }) {
  const [tab,     setTab]     = useState('login');
  const [screen,  setScreen]  = useState(() => {
    // Auto-navigate to reset if token present from URL
    try {
      if (window._pcResetToken || new URLSearchParams(window.location.search).get('token')) return 'reset';
    } catch(_) {}
    return 'auth';
  });   // 'auth' | 'forgot' | 'reset' | 'forgot_sent' | 'email_otp'
  const [form,    setForm]    = useState({ username:'', email:'', password:'' });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // Forgot / Reset state
  const [fpEmail,    setFpEmail]    = useState('');
  const [resetToken, setResetToken] = useState(() => {
    // Auto-fill from URL if user clicked reset link in email
    try {
      if (window._pcResetToken) {
        const t = window._pcResetToken;
        delete window._pcResetToken;
        return t;
      }
      const params = new URLSearchParams(window.location.search);
      return params.get('token') || '';
    } catch(_) { return ''; }
  });
  const [newPwd,     setNewPwd]     = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd,    setShowPwd]    = useState(false);
  const [phone,      setPhone]      = useState('');
  const [phoneNum,   setPhoneNum]   = useState(''); // just the number part
  const [selCountry, setSelCountry] = useState(() => COUNTRIES.find(c => c.code==='US')); // default USA
  const [otp,        setOtp]        = useState('');
  const [otpSent,    setOtpSent]    = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  // Phone OTP — resend cooldown + new user username setup
  const [phoneResendCD,   setPhoneResendCD]   = useState(0);
  const [phoneNeedsUser,  setPhoneNeedsUser]  = useState(false);
  const [phoneUsername,   setPhoneUsername]   = useState('');
  const [verifiedPhone,   setVerifiedPhone]   = useState('');
  const [devOtp,          setDevOtp]          = useState('');  // shown only in dev mode

  // Phone resend countdown
  useEffect(() => {
    if (phoneResendCD <= 0) return;
    const t = setTimeout(() => setPhoneResendCD(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneResendCD]);

  // Email OTP verification state (for registration)
  const [emailOtp,         setEmailOtp]         = useState('');
  const [emailOtpLoading,  setEmailOtpLoading]   = useState(false);
  const [resendCooldown,   setResendCooldown]    = useState(0);
  const [pendingRegForm,   setPendingRegForm]     = useState(null);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const inp = {
    width:'100%', padding:'13px 16px', borderRadius:10,
    border:`1px solid ${GD}`, background:'#1A1000', color:'#F0F0F0',
    fontSize:15, fontFamily:'inherit', outline:'none',
    boxSizing:'border-box', marginBottom:12,
  };

  // ── Send Email OTP ─────────────────────────────────────
  const handleSendEmailOtp = useCallback(async (email) => {
    setEmailOtpLoading(true); setError('');
    try {
      const res = await auth.sendEmailOtp(email);
      if (res.success) {
        setResendCooldown(RESEND_COOLDOWN);
        setSuccess(`Verification code sent to ${email}. Check your inbox and spam folder.`);
      } else if (res.setup_required) {
        setError('Email service not set up on this server. Please contact the administrator.');
      } else {
        setError(res.message || 'Failed to send OTP. Please check the email address and try again.');
      }
    } catch (e) {
      // Session G1: Surface real backend error message instead of generic "Network error"
      // (server-rejected requests with valid JSON also throw, so we must check)
      const msg = e?.message || 'Network error. Please try again.';
      setError(msg);
    }
    setEmailOtpLoading(false);
  }, []);

  const handleResendEmailOtp = async () => {
    if (resendCooldown > 0) return;
    setEmailOtp('');
    await handleSendEmailOtp(form.email);
  };

  // ── Verify Email OTP ───────────────────────────────────
  const handleVerifyEmailOtp = async () => {
    if (!emailOtp || emailOtp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    setEmailOtpLoading(true); setError('');
    try {
      const res = await auth.verifyEmailOtp(form.email, emailOtp);
      if (res.success) {
        setSuccess('Email verified! Creating your account...');
        // Auto-proceed to registration
        await completeRegistration(pendingRegForm);
      } else {
        setError(res.message || 'Invalid OTP. Please try again.');
      }
    } catch (e) {
      // Session G1: Surface real backend error message
      const msg = e?.message || 'Network error. Please try again.';
      setError(msg);
    }
    setEmailOtpLoading(false);
  };

  // ── Complete Registration after email verify ───────────
  const completeRegistration = async (regForm) => {
    setLoading(true); setError('');
    try {
      const res = await auth.register(regForm);
      if (res.success) {
        onLogin(res.user);
      } else {
        setError(res.message || 'Registration failed. Please try again.');
        setScreen('auth');
      }
    } catch (e) { setError(e.message); setScreen('auth'); }
    setLoading(false);
  };

  // ── Login / Register ───────────────────────────────────
  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      if (tab === 'login') {
        const res = await auth.login({ email: form.email, password: form.password });
        if (res.success) onLogin(res.user);
        else setError(res.message || 'Failed. Try again.');
      } else {
        // REGISTRATION: send email OTP first
        const body = { username: form.username, email: form.email, password: form.password };
        // Basic validation before sending OTP
        if (!form.username || form.username.length < 3) { setError('Username must be at least 3 characters'); setLoading(false); return; }
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Enter a valid email address'); setLoading(false); return; }
        if (!form.password || form.password.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }
        setPendingRegForm(body);
        setLoading(false);
        await handleSendEmailOtp(form.email);
        setScreen('email_otp');
        return;
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  // ── Forgot Password ────────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault();
    if (!fpEmail) { setError('Enter your email'); return; }
    setLoading(true); setError('');
    try {
      const res = await auth.forgotPassword(fpEmail);
      if (res.success) setScreen('forgot_sent');
      else setError(res.message || 'Email not found');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── Reset Password ─────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { setError('Passwords do not match'); return; }
    if (newPwd.length < 8)     { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await auth.resetPassword(resetToken, newPwd);
      if (res.success) { setSuccess('Password reset! Please login.'); setScreen('auth'); setTab('login'); }
      else setError(res.message || 'Invalid or expired token');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const btn = {
    width:'100%', padding:'14px', fontWeight:800, fontSize:16, borderRadius:12,
    border:'none', cursor:'pointer', fontFamily:'inherit', letterSpacing:1,
    background:`linear-gradient(135deg,${GD},${G},${GL})`, color:'#0D0A00',
    opacity: loading ? 0.7 : 1, transition:'opacity 0.2s',
  };

  return (
    <div style={{ minHeight:'100vh', background:'#080600', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:20, fontFamily:"'Inter','Segoe UI',sans-serif" }}>
      <style>{`
        @keyframes slideUp  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer  { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        *{box-sizing:border-box}
      `}</style>

      <div style={{ animation:'slideUp 0.5s ease', width:'100%', maxWidth:390 }}>
        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ display:'inline-block', marginBottom:12 }}><Logo size={80}/></div>
          <div style={{ fontSize:26, fontWeight:900, letterSpacing:3, background:`linear-gradient(90deg,${GD},${G},${GL},${G})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundSize:'200%', animation:'shimmer 3s linear infinite' }}>
            PAPER CRYPTO
          </div>
          <div style={{ color:'#666', fontSize:11, letterSpacing:2, marginTop:4 }}>SIMULATE · LEARN · MASTER</div>
        </div>

        {/* ── FORGOT SENT ── */}
        {screen === 'forgot_sent' && (
          <div style={{ background:'#111', borderRadius:14, padding:28, border:'1px solid #2A2000', textAlign:'center' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📧</div>
            <div style={{ color:G, fontWeight:800, fontSize:17, marginBottom:8 }}>Check Your Email</div>
            <div style={{ color:'#888', fontSize:13, marginBottom:20, lineHeight:1.6 }}>
              We sent a password reset link to <span style={{ color:G }}>{fpEmail}</span>.<br/>
              Copy the reset token from the email and paste it below.
            </div>
            <input style={inp} placeholder="Paste reset token here" value={resetToken}
              onChange={e => setResetToken(e.target.value)}
              onKeyDown={e => { if (e.key==='Enter') { if (resetToken) setScreen('reset'); else setError('Paste the reset token first'); }}}/>
            <button style={btn} onClick={() => { if (resetToken) setScreen('reset'); else setError('Paste the reset token first'); }}>
              Continue →
            </button>
            <div style={{ color:'#555', fontSize:12, marginTop:12, cursor:'pointer' }} onClick={() => setScreen('auth')}>← Back to Login</div>
          </div>
        )}

        {/* ── RESET PASSWORD ── */}
        {screen === 'reset' && (
          <div style={{ background:'#111', borderRadius:14, padding:24, border:'1px solid #2A2000' }}>
            <div style={{ color:G, fontWeight:800, fontSize:17, marginBottom:16, textAlign:'center' }}>🔑 Set New Password</div>
            {error && <div style={{ color:'#F6465D', fontSize:13, marginBottom:10, textAlign:'center' }}>{error}</div>}
            <form onSubmit={handleReset}>
              <input style={inp} type={showPwd ? 'text' : 'password'} placeholder="New password (min 6 chars)" value={newPwd}
                onChange={e => setNewPwd(e.target.value)} required/>
              <input style={inp} type="password" placeholder="Confirm new password" value={confirmPwd}
                onChange={e => setConfirmPwd(e.target.value)} required/>
              <button type="submit" disabled={loading} style={btn}>
                {loading ? '⏳ Resetting...' : '🔓 Reset Password'}
              </button>
            </form>
            <div style={{ color:'#555', fontSize:12, marginTop:12, textAlign:'center', cursor:'pointer' }} onClick={() => setScreen('auth')}>← Back to Login</div>
          </div>
        )}

        {/* ── FORGOT PASSWORD ── */}
        {screen === 'forgot' && (
          <div style={{ background:'#111', borderRadius:14, padding:24, border:'1px solid #2A2000' }}>
            <div style={{ color:G, fontWeight:800, fontSize:17, marginBottom:8, textAlign:'center' }}>🔐 Forgot Password</div>
            <div style={{ color:'#666', fontSize:12, marginBottom:16, textAlign:'center' }}>{"Enter your email and we'll send a reset link."}</div>
            {error && <div style={{ color:'#F6465D', fontSize:13, marginBottom:10, textAlign:'center' }}>{error}</div>}
            <form onSubmit={handleForgot}>
              <input style={inp} type="email" placeholder="Your email address" value={fpEmail}
                onChange={e => setFpEmail(e.target.value)} required/>
              <button type="submit" disabled={loading} style={btn}>
                {loading ? '⏳ Sending...' : '📧 Send Reset Link'}
              </button>
            </form>
            <div style={{ color:'#555', fontSize:12, marginTop:14, textAlign:'center', cursor:'pointer' }} onClick={() => { setScreen('auth'); setError(''); }}>← Back to Login</div>
          </div>
        )}

        {/* ── EMAIL OTP VERIFICATION (registration) ── */}
        {screen === 'email_otp' && (
          <div style={{ background:'#111', borderRadius:14, padding:24, border:'1px solid #2A2000' }}>
            <div style={{ textAlign:'center', marginBottom:16 }}>
              <div style={{ fontSize:44, marginBottom:8 }}>📧</div>
              <div style={{ color:G, fontWeight:900, fontSize:17, marginBottom:6 }}>Verify Your Email</div>
              <div style={{ color:'#888', fontSize:12, lineHeight:1.6 }}>
                We sent a 6-digit code to<br/>
                <span style={{ color:G, fontWeight:700 }}>{form.email}</span>
              </div>
            </div>

            {error  && <div style={{ color:'#F6465D', fontSize:13, marginBottom:10, textAlign:'center', background:'#F6465D15', padding:'8px 12px', borderRadius:8 }}>{error}</div>}
            {success && !error && <div style={{ color:'#0ECB81', fontSize:12, marginBottom:10, textAlign:'center' }}>{success}</div>}

            {/* OTP input */}
            <input
              style={{ ...inp, textAlign:'center', fontSize:28, fontWeight:900, letterSpacing:16, fontFamily:'monospace' }}
              type="number"
              placeholder="000000"
              value={emailOtp}
              onChange={e => setEmailOtp(e.target.value.slice(0,6))}
              inputMode="numeric"
              maxLength={6}
              autoFocus
              onKeyDown={e => { if (e.key==='Enter' && emailOtp.length===6 && !emailOtpLoading && !loading) handleVerifyEmailOtp(); }}
            />

            {/* Verify button */}
            <button
              disabled={emailOtpLoading || loading || emailOtp.length !== 6}
              onClick={handleVerifyEmailOtp}
              style={{ width:'100%', padding:'14px', fontWeight:800, fontSize:15, borderRadius:12,
                border:'none', cursor: emailOtp.length===6 ? 'pointer' : 'not-allowed', fontFamily:'inherit',
                background: emailOtp.length===6 ? `linear-gradient(135deg,${GD},${G},${GL})` : '#333',
                color: emailOtp.length===6 ? '#0D0A00' : '#666', marginBottom:12,
                opacity: (emailOtpLoading || loading) ? 0.7 : 1 }}>
              {(emailOtpLoading || loading) ? '⏳ Verifying...' : '✅ Verify & Create Account'}
            </button>

            {/* Resend OTP */}
            <div style={{ textAlign:'center', marginBottom:12 }}>
              {resendCooldown > 0 ? (
                <span style={{ color:'#555', fontSize:12 }}>Resend OTP in {resendCooldown}s</span>
              ) : (
                <span
                  style={{ color:G, fontSize:12, cursor:'pointer', fontWeight:600 }}
                  onClick={handleResendEmailOtp}>
                  🔄 Resend OTP
                </span>
              )}
            </div>

            {/* OTP expiry notice */}
            <div style={{ color:'#555', fontSize:11, textAlign:'center', marginBottom:12 }}>
              ⏱ Code expires in 5 minutes · Check spam folder if not received
            </div>

            <div style={{ color:'#555', fontSize:12, textAlign:'center', cursor:'pointer' }}
              onClick={() => { setScreen('auth'); setEmailOtp(''); setError(''); setSuccess(''); }}>
              ← Back · Use a different email
            </div>
          </div>
        )}

        {/* ── PHONE OTP — REAL SMS LOGIN ── */}
        {screen === 'phone_otp' && !phoneNeedsUser && (
          <div style={{ background:'#111', borderRadius:14, padding:24, border:'1px solid #2A2000' }}>
            <div style={{ textAlign:'center', marginBottom:16 }}>
              <div style={{ fontSize:40, marginBottom:8 }}>📱</div>
              <div style={{ color:G, fontWeight:900, fontSize:17, marginBottom:4 }}>Phone Login</div>
              <div style={{ color:'#888', fontSize:12 }}>
                {!otpSent ? 'Enter your mobile number to receive an OTP' : `OTP sent to ${selCountry?.dial}${phoneNum}`}
              </div>
            </div>

            {error  && <div style={{ color:'#F6465D', fontSize:13, marginBottom:10, textAlign:'center', background:'#F6465D15', padding:'8px 12px', borderRadius:8 }}>{error}</div>}
            {success && !error && <div style={{ color:'#0ECB81', fontSize:12, marginBottom:10, textAlign:'center' }}>{success}</div>}

            {!otpSent ? (
              <>
                {/* Country code picker + phone number */}
                <div style={{ marginBottom:10 }}>
                  <CountryCodePicker
                    value={selCountry?.dial || '+1'}
                    onChange={(country) => {
                      setSelCountry(country);
                      setPhone(country.dial + phoneNum);
                    }}
                    phoneNumber={phoneNum}
                    onPhoneChange={(num) => {
                      setPhoneNum(num);
                      setPhone((selCountry?.dial || '+1') + num.replace(/\s/g,''));
                    }}
                    disabled={otpLoading}
                  />
                </div>
                <div style={{ color:'#555', fontSize:11, marginBottom:12, textAlign:'center' }}>
                  {selCountry?.name} · {selCountry?.dial} · Enter your mobile number
                </div>
                <button
                  disabled={otpLoading || !phoneNum.trim()}
                  onClick={async () => {
                    setOtpLoading(true); setError(''); setSuccess(''); setDevOtp('');
                    try {
                      const apiBase = (window.location.hostname === 'localhost' ? (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') : 'https://api.papercrypto.app/api');
                      const res = await fetch(`${apiBase}/auth/phone/send-otp`, {
                        method:'POST', headers:{'Content-Type':'application/json'},
                        body: JSON.stringify({ phone: phone.trim() })
                      });
                      const data = await res.json();
                      if (data.success) {
                        setOtpSent(true);
                        setPhoneResendCD(60);
                        setSuccess(data.message);
                        if (data.dev_otp) { setDevOtp(data.dev_otp); }
                      } else {
                        setError(data.message || 'Failed to send OTP');
                      }
                    } catch (e) { setError(e?.message || 'Network error. Please try again.'); }
                    setOtpLoading(false);
                  }}
                  style={{ ...btn, opacity: (otpLoading || !phoneNum.trim()) ? 0.6 : 1, cursor: !phoneNum.trim() ? 'not-allowed' : 'pointer' }}>
                  {otpLoading ? '⏳ Sending SMS...' : '📤 Send OTP'}
                </button>
              </>
            ) : (
              <>
                {/* OTP input with autocomplete="one-time-code" for auto-fill on Android/iOS */}
                <input
                  style={{ ...inp, textAlign:'center', fontSize:28, fontWeight:900, letterSpacing:16, fontFamily:'monospace' }}
                  type="number"
                  placeholder="000000"
                  value={otp}
                  onChange={e => setOtp(e.target.value.slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  onKeyDown={e => { if (e.key==='Enter' && otp.length===6 && !otpLoading) document.getElementById('phone-verify-btn')?.click(); }}
                />

                {/* Dev mode OTP hint */}
                {devOtp && (
                  <div style={{ color:'#F5C518', fontSize:11, textAlign:'center', marginBottom:8,
                    background:'#1A1000', padding:'6px 12px', borderRadius:8, border:'1px solid #C9960C',
                    fontFamily:'monospace', letterSpacing:2, fontWeight:700 }}>
                    [DEV] OTP: {devOtp}
                  </div>
                )}

                {/* Verify button */}
                <button
                  id="phone-verify-btn"
                  disabled={otpLoading || otp.length !== 6}
                  onClick={async () => {
                    setOtpLoading(true); setError(''); setSuccess('');
                    try {
                      const apiBase = (window.location.hostname === 'localhost' ? (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') : 'https://api.papercrypto.app/api');
                      const res = await fetch(`${apiBase}/auth/phone/verify-otp`, {
                        method:'POST', headers:{'Content-Type':'application/json'},
                        body: JSON.stringify({ phone: phone.trim(), otp })
                      });
                      const data = await res.json();
                      if (data.success && data.needs_username) {
                        // New user — need username setup
                        setVerifiedPhone(phone.trim());
                        setPhoneNeedsUser(true);
                        setError(''); setSuccess('');
                      } else if (data.success) {
                        onLogin(data.user);
                      } else {
                        setError(data.message || 'Invalid OTP');
                      }
                    } catch (e) { setError(e?.message || 'Network error. Please try again.'); }
                    setOtpLoading(false);
                  }}
                  style={{ ...btn,
                    background: otp.length === 6 ? `linear-gradient(135deg,${GD},${G},${GL})` : '#333',
                    color: otp.length === 6 ? '#0D0A00' : '#666',
                    opacity: otpLoading ? 0.7 : 1, cursor: otp.length !== 6 ? 'not-allowed' : 'pointer' }}>
                  {otpLoading ? '⏳ Verifying...' : '✅ Verify & Login'}
                </button>

                {/* Resend with cooldown */}
                <div style={{ textAlign:'center', marginTop:12 }}>
                  {phoneResendCD > 0 ? (
                    <span style={{ color:'#555', fontSize:12 }}>Resend OTP in {phoneResendCD}s</span>
                  ) : (
                    <span style={{ color:G, fontSize:12, cursor:'pointer', fontWeight:600 }}
                      onClick={async () => {
                        setOtp(''); setError(''); setSuccess(''); setDevOtp('');
                        setOtpLoading(true);
                        try {
                          const apiBase = (window.location.hostname === 'localhost' ? (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') : 'https://api.papercrypto.app/api');
                          const res = await fetch(`${apiBase}/auth/phone/send-otp`, {
                            method:'POST', headers:{'Content-Type':'application/json'},
                            body: JSON.stringify({ phone: phone.trim() })
                          });
                          const data = await res.json();
                          if (data.success) { setPhoneResendCD(60); setSuccess('New OTP sent!'); if (data.dev_otp) setDevOtp(data.dev_otp); }
                          else setError(data.message || 'Failed to resend');
                        } catch (e) { setError(e?.message || 'Network error'); }
                        setOtpLoading(false);
                      }}>
                      🔄 Resend OTP
                    </span>
                  )}
                </div>

                <div style={{ color:'#555', fontSize:11, textAlign:'center', marginTop:8 }}>
                  ⏱ Expires in 5 minutes · Check SMS inbox
                </div>
              </>
            )}

            <div style={{ color:'#555', fontSize:12, marginTop:16, textAlign:'center', cursor:'pointer' }}
              onClick={() => { setScreen('auth'); setOtpSent(false); setOtp(''); setPhone(''); setPhoneNum(''); setError(''); setSuccess(''); setDevOtp(''); }}>
              ← Back to Login
            </div>
          </div>
        )}

        {/* ── PHONE NEW USER — USERNAME SETUP ── */}
        {screen === 'phone_otp' && phoneNeedsUser && (
          <div style={{ background:'#111', borderRadius:14, padding:24, border:'1px solid #2A2000' }}>
            <div style={{ textAlign:'center', marginBottom:16 }}>
              <div style={{ fontSize:40, marginBottom:8 }}>🎉</div>
              <div style={{ color:G, fontWeight:900, fontSize:17, marginBottom:4 }}>Choose Your Username</div>
              <div style={{ color:'#888', fontSize:12 }}>Phone verified! One last step to complete setup.</div>
            </div>

            {error && <div style={{ color:'#F6465D', fontSize:13, marginBottom:10, textAlign:'center', background:'#F6465D15', padding:'8px 12px', borderRadius:8 }}>{error}</div>}

            <input
              style={inp}
              placeholder="Choose a username (min 3 chars)"
              value={phoneUsername}
              onChange={e => setPhoneUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20))}
              autoFocus
              onKeyDown={e => { if (e.key==='Enter' && phoneUsername.length>=3 && !otpLoading) document.getElementById('phone-setup-btn')?.click(); }}
            />
            <div style={{ color:'#555', fontSize:11, marginBottom:12 }}>
              Letters, numbers, underscore only · e.g. crypto_trader99
            </div>

            <button
              id="phone-setup-btn"
              disabled={otpLoading || phoneUsername.length < 3}
              onClick={async () => {
                setOtpLoading(true); setError('');
                try {
                  const apiBase = (window.location.hostname === 'localhost' ? (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') : 'https://api.papercrypto.app/api');
                  const res = await fetch(`${apiBase}/auth/phone/setup-profile`, {
                    method:'POST', headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({ phone: verifiedPhone || phone.trim(), username: phoneUsername })
                  });
                  const data = await res.json();
                  if (data.success && !data.needs_username) {
                    onLogin(data.user);
                  } else {
                    setError(data.message || 'Username taken. Try another.');
                  }
                } catch (e) { setError(e?.message || 'Network error. Please try again.'); }
                setOtpLoading(false);
              }}
              style={{ ...btn, opacity: (otpLoading || phoneUsername.length < 3) ? 0.6 : 1,
                cursor: phoneUsername.length < 3 ? 'not-allowed' : 'pointer' }}>
              {otpLoading ? '⏳ Creating Account...' : '🚀 Create Account & Login'}
            </button>

            <div style={{ color:'#555', fontSize:12, marginTop:14, textAlign:'center', cursor:'pointer' }}
              onClick={() => { setPhoneNeedsUser(false); setOtpSent(false); setOtp(''); setPhoneUsername(''); setError(''); }}>
              ← Use a different phone number
            </div>
          </div>
        )}

        {/* ── LOGIN / REGISTER ── */}
        {screen === 'auth' && (
          <>
            {success && <div style={{ background:'#0ECB8122', border:'1px solid #0ECB81', borderRadius:10, padding:12, color:'#0ECB81', fontSize:13, textAlign:'center', marginBottom:14 }}>{success}</div>}

            {/* Tab switcher */}
            <div style={{ display:'flex', background:'#111', borderRadius:12, padding:4, marginBottom:20, border:'1px solid #2A2000' }}>
              {TABS.map(t => (
                <div key={t} onClick={() => { setTab(t); setError(''); setSuccess(''); }}
                  style={{ flex:1, padding:'10px', textAlign:'center', borderRadius:10, cursor:'pointer', fontSize:13, fontWeight:700, textTransform:'capitalize', transition:'all 0.2s',
                    background: tab === t ? `linear-gradient(135deg,${GD},${G})` : 'transparent',
                    color:      tab === t ? '#0D0A00' : '#888' }}>
                  {t}
                </div>
              ))}
            </div>

            {error && <div style={{ color:'#F6465D', fontSize:13, marginBottom:12, textAlign:'center' }}>{error}</div>}

            <form onSubmit={handleAuth}>
              {tab === 'register' && (
                <input style={inp} placeholder="Username" value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })} required/>
              )}
              {/* S-096 (2026-05-31): Login accepts EMAIL or USERNAME (case-insensitive).
                  Register still requires email (type=email + placeholder). Backend detects
                  identifier type by '@' presence — usernames cannot have '@' per validateRegister. */}
              <input style={inp}
                type={tab === 'login' ? 'text' : 'email'}
                placeholder={tab === 'login' ? 'Email or username' : 'Email address'}
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} required/>
              <div style={{ position:'relative', marginBottom:12 }}>
                <input style={{ ...inp, marginBottom:0, paddingRight:44 }}
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Password" value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })} required/>
                <span onClick={() => setShowPwd(v=>!v)}
                  style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)',
                    cursor:'pointer', fontSize:16, userSelect:'none', color:'#888' }}>
                  {showPwd ? '👁️' : '🙈'}
                </span>
              </div>

              <button type="submit" disabled={loading} style={btn}>
                {loading ? '⏳ Please wait...' : tab === 'login' ? '🔓 Login' : '🚀 Create Account'}
              </button>
            </form>

            {tab === 'login' && (
              <div style={{ textAlign:'center', marginTop:14 }}>
                <span style={{ color:'#555', fontSize:12, cursor:'pointer' }}
                  onClick={() => { setScreen('forgot'); setError(''); }}>
                  Forgot your password?
                </span>
              </div>
            )}

            {/* ── Social Login Divider ── */}
            <div style={{ display:'flex', alignItems:'center', gap:10, margin:'18px 0 14px' }}>
              <div style={{ flex:1, height:1, background:'#2A2000' }}/>
              <span style={{ color:'#555', fontSize:11, whiteSpace:'nowrap' }}>or continue with</span>
              <div style={{ flex:1, height:1, background:'#2A2000' }}/>
            </div>

            {/* Google OAuth - GSI One Tap */}
            <button
              type="button"
              id="google-signin-btn"
              onClick={async () => {
                // Load Google Identity Services if not loaded
                if (!window.google?.accounts) {
                  const s = document.createElement('script');
                  s.src = 'https://accounts.google.com/gsi/client';
                  s.async = true;
                  document.head.appendChild(s);
                  await new Promise(r => { s.onload = r; setTimeout(r, 3000); });
                }
                const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
                if (!clientId) {
                  setError('Google login not configured. Use email/password or Phone OTP.');
                  return;
                }
                if (window.google?.accounts) {
                  // S-174 (2026-07-30): hold a ref to the popup so the callback can REMOVE it
                  // after login. Previously the box was only cleared by a manual page refresh.
                  let gsiBox = null;
                  window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: async (response) => {
                      setLoading(true);
                      // S-174: dismiss the Google popup box immediately on callback (success OR fail),
                      // so it never lingers on screen requiring a manual refresh.
                      try { if (gsiBox) gsiBox.remove(); } catch(_) {}
                      try {
                        const apiBase = (window.location.hostname === 'localhost' ? (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') : 'https://api.papercrypto.app/api');
                        const res = await fetch(`${apiBase}/auth/google`, {
                          method:'POST',
                          headers:{'Content-Type':'application/json'},
                          body: JSON.stringify({ id_token: response.credential }),
                        });
                        const data = await res.json();
                        if (data.success) { onLogin(data.user); localStorage.setItem('pc_token', data.token); }
                        else setError(data.message || 'Google login failed');
                      } catch (e) { setError(e?.message || 'Google login failed. Please try again.'); }
                      setLoading(false);
                    },
                    auto_select: false,
                  });
                  // Use renderButton (FedCM/One Tap is unreliable). S-174: do NOT also call
                  // prompt() — that popped a SECOND One Tap box alongside this one. One box only.
                  const btnDiv = document.createElement('div');
                  gsiBox = btnDiv;
                  btnDiv.id = 'g_id_signin_btn';
                  btnDiv.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:99999;background:rgba(0,0,0,0.8);padding:40px;border-radius:16px;';
                  const closeBtn = document.createElement('div');
                  closeBtn.innerHTML = '✕ Close';
                  closeBtn.style.cssText = 'color:#888;text-align:center;margin-top:16px;cursor:pointer;font-size:14px;';
                  closeBtn.onclick = () => btnDiv.remove();
                  document.body.appendChild(btnDiv);
                  btnDiv.appendChild(closeBtn);
                  window.google.accounts.id.renderButton(btnDiv, {
                    type: 'standard', theme: 'filled_black', size: 'large', width: 280, text: 'signin_with'
                  });
                  // S-174: prompt() removed — it created a duplicate One Tap box.
                } else {
                  setError('Google login unavailable. Please add REACT_APP_GOOGLE_CLIENT_ID to .env');
                }
              }}
              style={{ width:'100%', padding:'12px', borderRadius:10, border:'1px solid #333',
                background:'#1A1A1A', color:'white', fontWeight:700, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                fontSize:14, marginBottom:8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Phone OTP — Hidden via FEATURES flag (S-100) */}
            {FEATURES.PHONE_OTP_ENABLED && (
              <button
                type="button"
                onClick={() => setScreen('phone_otp')}
                style={{ width:'100%', padding:'12px', borderRadius:10, border:'1px solid #333',
                  background:'#1A1A1A', color:'white', fontWeight:700, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                  fontSize:14, marginBottom:8 }}>
                📱 Continue with Phone (OTP)
              </button>
            )}

            {/* G1.7: Apple Sign-In — Apple branding rules: black bg, white text, Apple logo */}
            <button
              type="button"
              onClick={async () => {
                setError('');
                // Lazy-load Apple JS SDK
                if (!window.AppleID?.auth) {
                  const s = document.createElement('script');
                  s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
                  s.async = true;
                  document.head.appendChild(s);
                  await new Promise(r => { s.onload = r; setTimeout(r, 3000); });
                }
                const serviceId = process.env.REACT_APP_APPLE_SERVICE_ID;
                if (!serviceId) {
                  setError('Apple Sign-In not configured. Add REACT_APP_APPLE_SERVICE_ID to .env');
                  return;
                }
                if (!window.AppleID?.auth) {
                  setError('Apple Sign-In SDK failed to load. Try refreshing.');
                  return;
                }
                try {
                  window.AppleID.auth.init({
                    clientId: serviceId,                    // Service ID (NOT app bundle ID)
                    scope: 'name email',
                    redirectURI: window.location.origin,    // Must match registered URI in Apple portal
                    state: 'pc_apple_' + Date.now(),
                    usePopup: true,                          // Popup → returns to JS callback
                  });
                  const data = await window.AppleID.auth.signIn();
                  // data = { authorization: { id_token, code, state }, user?: { name, email } (1st sign-in only) }
                  const id_token = data?.authorization?.id_token;
                  if (!id_token) {
                    setError('Apple Sign-In was cancelled.');
                    return;
                  }
                  setLoading(true);
                  try {
                    const res = await authExtra.appleLogin(id_token, data.user);
                    if (res.success) {
                      onLogin(res.user);
                      localStorage.setItem('pc_token', res.token);
                      if (res.refreshToken) localStorage.setItem('pc_refresh', res.refreshToken);
                    } else {
                      setError(res.message || 'Apple Sign-In failed');
                    }
                  } catch (e) {
                    setError(e?.message || 'Apple Sign-In failed.');
                  }
                  setLoading(false);
                } catch (e) {
                  // User cancelled or popup blocked
                  if (e?.error !== 'popup_closed_by_user') {
                    setError(e?.message || 'Apple Sign-In failed.');
                  }
                }
              }}
              style={{ width:'100%', padding:'12px', borderRadius:10, border:'1px solid #000',
                background:'#000', color:'white', fontWeight:700, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                fontSize:14, marginBottom:8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Continue with Apple
            </button>

            {/* G1.4: Twitter/Facebook/Instagram/Threads OAuth buttons removed.
                Following Binance-level standard: Email + Phone + Google + Apple only.
                Apple Sign-In coming in G1.7. See backend/routes/_deprecated/socialAuth.js */}

            <div style={{ textAlign:'center', marginTop:16, color:'#444', fontSize:11, lineHeight:1.8 }}>
              Paper Crypto uses virtual funds only.<br/>No real money involved. For educational use.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
